import { ProductCard } from '@/components/brand';
import type { CMSProductCard, CMSTrendingProducts } from '@/lib/cms/types';
import {
  getGalletPlpProductCardProps,
  resolveCarouselProductKind,
} from '@/lib/product/galletPlpProductCardProps';
import { getProductPlaceholder } from '@/lib/product/placeholders';
import { devLog } from '@/lib/utils/devLog';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Card } from '../Card';
import { Cta } from '../Card/components/Cta';
import { CMSSection } from '../CmsSection';
import { FreeTextContent } from '../FreeTextContent';
import { Heading, type HeadingData } from '../Heading';
import { MediaWrapper, type MediaProp } from '../MediaWrapper';
import { getPlaceholderServiceRoutingProps } from '@/lib/cms/extraData';

import { PlaceholderService } from '../PlaceholderService';

import styles from './DoubleCard.module.scss';

/**
 * Base content item interface for polymorphic rendering
 */
export interface ContentItemBase {
  __typename?: string;
  sys: { id: string };
}

/**
 * Card content type
 */
export interface CardContentItem extends ContentItemBase {
  __typename: 'Card';
  // Card-specific props would go here
  [key: string]: unknown;
}

/**
 * Image content type
 */
export interface ImageContentItem extends ContentItemBase {
  __typename: 'ImageWithFocalPoint';
  image?: { url?: string | null } | null;
  alt?: string | null;
  [key: string]: unknown;
}

/**
 * Heading content type
 */
export interface HeadingContentItem extends ContentItemBase {
  __typename: 'Heading';
  text?: string | null;
  headingType?: string | null;
  [key: string]: unknown;
}

/**
 * ProductCard content type
 */
export interface ProductCardContentItem extends ContentItemBase {
  __typename: 'ProductCard';
  [key: string]: unknown;
}

/**
 * PlaceholderService content type
 */
export interface PlaceholderServiceContentItem extends ContentItemBase {
  __typename: 'PlaceholderService';
  type?: string | null;
  [key: string]: unknown;
}

/**
 * FreeTextContent type
 */
export interface FreeTextContentItem extends ContentItemBase {
  __typename: 'FreeTextContent';
  freeTextContent?: { json?: unknown } | null;
  [key: string]: unknown;
}

/**
 * SliderProductsManual / SliderProductsAutomatic nested inside a grid cell.
 * Populated by the listing orchestrator with `products` from Algolia.
 */
export interface SliderProductsContentItem extends ContentItemBase {
  __typename: 'SliderProductsManual' | 'SliderProductsAutomatic' | 'AutomaticCarousel';
  [key: string]: unknown;
}

export type ContentGroupItem =
  | CardContentItem
  | ImageContentItem
  | HeadingContentItem
  | ProductCardContentItem
  | PlaceholderServiceContentItem
  | FreeTextContentItem
  | SliderProductsContentItem;

export interface DoubleCardProps {
  /**
   * Collection of content items to render
   * Legacy format (from raw Contentful): itemsCollection.items
   */
  itemsCollection?: {
    items: Array<ContentGroupItem | null>;
  } | null;
  /**
   * Direct items array (from adapter)
   * Takes precedence over itemsCollection if both provided
   */
  items?: Array<ContentGroupItem | null>;
  /** Optional ID for anchor linking */
  id?: string | null;
  /** Content group type */
  type?: string | null;
  /** Extra data record for CMS hydration */
  extraDataRecord?: Record<string, unknown>;
  /** Custom render function for content items */
  renderItem?: (item: ContentGroupItem, index: number) => ReactNode;
  /** Optional className */
  className?: string;
}

/**
 * Default item renderer - renders based on __typename
 * Override with renderItem prop for custom rendering
 * Exported for use by related layout components (DoubleCardWide, etc.)
 */
export function renderCollectionItem(
  item: ContentGroupItem,
  extraDataRecord?: Record<string, unknown>
): ReactNode {
  devLog('[Grid/DoubleCard] renderCollectionItem:', item.__typename, item);

  switch (item.__typename) {
    case 'Heading':
      return <Heading Heading={item as HeadingData} className={styles.fullHeight} />;
    case 'Card':
      return (
        <Card
          data={item}
          type={(item as CardContentItem).type as import('../Card').CardType}
          className={styles.fullHeight}
        />
      );
    case 'ImageWithFocalPoint':
      // Bare images render at their intrinsic ratio — no banner chrome.
      // Wrapping in `<Card type="MediaBanner">` forced a cover-crop that
      // sliced wide assets (e.g. wordmark logos) horizontally.
      return <MediaWrapper media={item as MediaProp} mediaType="Auto" />;
    case 'ProductCard':
      // ProductCard can render through Card component
      return <Card data={item} type="MediaAndBottomText" className={styles.fullHeight} />;
    case 'PlaceholderService': {
      const serviceItem = item as PlaceholderServiceContentItem;
      return (
        <PlaceholderService
          {...serviceItem}
          {...getPlaceholderServiceRoutingProps(extraDataRecord)}
          type={serviceItem.type}
        />
      );
    }
    case 'FreeTextContent':
      return <FreeTextContent freeTextContent={item.freeTextContent} />;
    case 'SliderProductsManual':
    case 'SliderProductsAutomatic':
    case 'AutomaticCarousel': {
      // Sliders nested inside Grid cells (e.g. NFL Local Banner) act as a
      // featured-product tile — heading, one product card, CTA — not a
      // full carousel. Render compact so the tile height tracks its
      // sibling image cell rather than stacking the full Trending section.
      return <NestedProductTile data={item as unknown as CMSTrendingProducts} />;
    }
    default:
      return null;
  }
}

/**
 * Compact product-tile used when a SliderProductsManual sits inside a grid
 * cell. Renders the slider's heading + first product + CTA stacked,
 * matching the legacy "featured product" layout (e.g. NFL Local Banner).
 */
function NestedProductTile({ data }: { data: CMSTrendingProducts }) {
  const products = (data.products as CMSProductCard[] | undefined) ?? [];
  if (products.length === 0) return null;
  const product = products[0];

  return (
    <div className={styles.nestedProductTile}>
      {data.heading?.text && (
        <Heading
          Heading={{
            text: data.heading.text,
            headingType: data.heading.headingType ?? 'h2',
            headingSeoTag: data.heading.headingSeoTag ?? 'h2',
            mainColor: data.heading.mainColor ?? undefined,
            highlightColor: data.heading.highlightColor ?? undefined,
          }}
          className={styles.nestedProductHeading}
        />
      )}
      <div className={styles.nestedProductCard} data-testid="nested-product-card">
        <ProductCard
          {...getGalletPlpProductCardProps(
            resolveCarouselProductKind(product.productType, data.sliderType)
          )}
          variant="compact"
          product={{
            id: product.sys?.id,
            sku: product.sku || '',
            name: product.name || '',
            subtitle: product.subtitle || undefined,
            price: product.price || 0,
            currency: product.currency || 'USD',
            image: product.image?.url || getProductPlaceholder(product.productType),
            href: product.href || '#',
            // Intentionally omit variantId/ctaType — the per-card CTA button is
            // hidden via styles below; the slider's own CTA is the actionable
            // element in this nested grid-cell layout.
            label: product.label || undefined,
            material: product.material || undefined,
            productType: product.productType || undefined,
          }}
          showWishlist={false}
        />
      </div>
      {data.ctaText && data.ctaStyle && data.ctaStyle !== 'none' && data.ctaUrl && (
        <div className={styles.nestedProductCta}>
          <Cta
            url={data.ctaUrl}
            urlText={data.ctaText}
            urlType={data.ctaStyle}
            ariaLabel={data.ctaAriaLabel ?? undefined}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Get column class based on number of items
 */
function getColumnClass(itemCount: number): string {
  switch (itemCount) {
    case 2:
      return styles.col2;
    case 3:
      return styles.col3;
    case 4:
      return styles.col4;
    default:
      return styles.col2;
  }
}

/**
 * DoubleCard component
 *
 * Renders a row of content items in equal-width columns.
 * Typically used for 2 items side-by-side, but supports 3-4 items too.
 *
 * Supports content types:
 * - Card
 * - ImageWithFocalPoint
 * - ProductCard
 * - Heading
 * - PlaceholderService
 * - FreeTextContent
 *
 * @example
 * <DoubleCard
 *   itemsCollection={{
 *     items: [
 *       { __typename: 'Card', sys: { id: '1' }, ... },
 *       { __typename: 'Card', sys: { id: '2' }, ... },
 *     ]
 *   }}
 * />
 */
export function DoubleCard({
  itemsCollection,
  items: directItems,
  id,
  extraDataRecord,
  renderItem,
  className,
}: DoubleCardProps) {
  // Prefer direct items (adapter format), fallback to itemsCollection (legacy format)
  const rawItems = directItems ?? itemsCollection?.items ?? [];
  const items = rawItems.filter((item): item is ContentGroupItem => item !== null);

  if (items.length === 0) {
    return null;
  }

  const columnClass = getColumnClass(items.length);
  const itemRenderer = renderItem ?? ((item) => renderCollectionItem(item, extraDataRecord));

  const content = (
    <CMSSection data-testid="DoubleCardSection" className={className}>
      <div className={styles.wrapper} data-testid="DoubleCardWrapper">
        {items.map((item, index) => {
          const rendered = itemRenderer(item, index);
          return rendered ? (
            <div
              key={item.sys.id}
              className={clsx(styles.col, columnClass)}
              data-testid={`DoubleCardCol-${index}`}
            >
              {rendered}
            </div>
          ) : null;
        })}
      </div>
    </CMSSection>
  );

  // Wrap with ID if provided for anchor linking
  if (id) {
    return <div id={id}>{content}</div>;
  }

  return content;
}

DoubleCard.displayName = 'DoubleCard';
